/**
 * TierBadge — small pill that flags a business as Founding (gold) or
 * Premium (silver). Rendered on Directory cards, Spotlight cards, BOTW
 * carousel, and the business detail hero.
 *
 * Logic:
 *   - "founding"  → gold pill w/ 🏆 icon (highest tier)
 *   - "premium"   → silver pill w/ Sparkles icon (mid tier)
 *   - anything else → renders nothing (no visual noise for free tier)
 *
 * Tier is resolved from either the modern `subscription_tier` string OR the
 * legacy `is_founding_member` / `is_premium` booleans, matching how the
 * backend gates features across `subscriptions.py` and `featured.py`.
 */
import { Trophy, Sparkles } from "lucide-react";

function resolveTier(business) {
  if (!business) return null;
  const tier = (business.subscription_tier || "").toLowerCase();
  if (tier === "founding" || business.is_founding_member) return "founding";
  if (tier === "premium" || business.is_premium) return "premium";
  return null;
}

const SIZE_CLASSES = {
  xs: "text-[9px] px-1.5 py-0.5 gap-0.5",
  sm: "text-[10px] px-2 py-0.5 gap-1",
  md: "text-xs px-2.5 py-1 gap-1",
};

const ICON_SIZES = {
  xs: "w-2.5 h-2.5",
  sm: "w-3 h-3",
  md: "w-3.5 h-3.5",
};

export function TierBadge({ business, size = "sm", className = "" }) {
  const tier = resolveTier(business);
  if (!tier) return null;

  const isFounding = tier === "founding";
  const label = isFounding ? "Founding" : "Premium";
  const Icon = isFounding ? Trophy : Sparkles;

  const colorClasses = isFounding
    ? "bg-tbc-gold text-tbc-navy ring-1 ring-tbc-navy/10"
    : "bg-gradient-to-br from-gray-200 to-gray-300 text-gray-800 ring-1 ring-gray-400/40";

  return (
    <span
      data-testid={`tier-badge-${tier}`}
      className={`inline-flex items-center rounded-full font-semibold uppercase tracking-wider ${SIZE_CLASSES[size]} ${colorClasses} ${className}`}
      title={isFounding ? "Founding Business — an original supporter" : "Premium Business"}
    >
      <Icon className={ICON_SIZES[size]} />
      {label}
    </span>
  );
}

export default TierBadge;
